import { useCallback } from "react";

import { usePage } from "../context/PageContext";
import { usePageActions } from "./usePageActions";

import { PageGrid, Page } from "../types";
import { pageEndpoints } from "../api/endpoints";

function usePageOperations() {
    const { state } = usePage();
    const { updatePage: updatePageAction, setLoading, setError, setPages, setCurrentPage, deletePage: deletePageAction } = usePageActions();

    const createPage = useCallback(
        async (title: string, dashboardId: number, options?: { createdBy?: string; publishedToAll?: boolean }) => {
            try {
                setLoading(true);
                setError(null);

                const newPage = await pageEndpoints.createPage({
                    title,
                    dashboardId,
                    pagePrompt: "",
                    createdBy: options?.createdBy,
                    publishedToAll: options?.publishedToAll,
                });

                if (newPage) {
                    const currentPages = state.pages;
                    const updatedPages = [...currentPages, newPage];

                    setPages(updatedPages);

                    return newPage;
                }

                setError("Failed to create page");
                return undefined;
            } catch (error) {
                console.error("Error creating page:", error);
                setError("Failed to create page");
                return undefined;
            } finally {
                setLoading(false);
            }
        },
        [state, setPages, setCurrentPage, setLoading, setError]
    );

    const updatePage = useCallback(
        async (pageId: number, updates: Partial<Page>) => {
            const _page = await pageEndpoints.updatePage(pageId, undefined, updates);
            if (_page) updatePageAction(_page);
        },
        [updatePageAction]
    );

    const updatePageGrid = useCallback(
        async (pageId: number, pageTabId?: number, grid?: Partial<PageGrid>) => {
            const _page = await pageEndpoints.updatePage(pageId, pageTabId, { grid });
            if (_page) updatePageAction(_page);
        },
        [updatePageAction]
    );

    const deletePage = useCallback(
        async (pageId: number) => {
            try {
                setLoading(true);
                setError(null);

                const success = await pageEndpoints.deletePage(pageId);
                if (success) {
                    deletePageAction(pageId);
                    return true;
                } else {
                    setError("Failed to delete page");
                    return false;
                }
            } catch (error) {
                console.error("Error deleting page:", error);
                setError("Failed to delete page");
                return false;
            } finally {
                setLoading(false);
            }
        },
        [deletePageAction, setLoading, setError]
    );

    return { createPage, updatePage, updatePageGrid, deletePage };
}

export { usePageOperations };
